import { useEffect, useState } from 'react';
import { Autocomplete, TextField, type TextFieldProps } from '@mui/material';
import { apiFetch } from '../lib/api';

/**
 * Suburb type-ahead over the postcodes table. Choosing a suggestion hands back
 * the suburb, postcode and state together, which is how the legacy job and
 * client forms filled those fields. Free text is still allowed for anywhere the
 * table does not know.
 */

export interface PostcodeMatch {
    readonly suburb: string;
    readonly postcode: string;
    readonly state: string;
}

interface SuburbFieldProps {
    readonly value: string;
    readonly onChange: (suburb: string) => void;
    readonly onSelect: (match: PostcodeMatch) => void;
    readonly label?: string;
}

export function SuburbField({ value, onChange, onSelect, label = 'Suburb' }: SuburbFieldProps): React.JSX.Element {
    const [options, setOptions] = useState<readonly PostcodeMatch[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const text = value.trim();
        if (text.length < 2) {
            setOptions([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            setLoading(true);
            apiFetch<{ postcodes: readonly PostcodeMatch[] }>(`/postcodes?q=${encodeURIComponent(text)}`)
                .then((r) => {
                    if (!cancelled) {
                        setOptions(r.postcodes);
                    }
                })
                .catch(() => {
                    /* Suggestions are a convenience; typing still works. */
                })
                .finally(() => {
                    if (!cancelled) {
                        setLoading(false);
                    }
                });
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [value]);

    return (
        <Autocomplete<PostcodeMatch, false, false, true>
            freeSolo
            fullWidth
            size="small"
            options={options}
            // The server has already filtered and ranked; don't re-filter.
            filterOptions={(x) => x}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.suburb)}
            isOptionEqualToValue={(a, b) => a.suburb === b.suburb && a.postcode === b.postcode && a.state === b.state}
            renderOption={(props, o) => {
                // MUI types the option props' `key` as any. It has to be pulled out
                // rather than spread (React 19 warns), and our own key is stable anyway.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars
                const { key: _muiKey, ...rest } = props;
                return (
                    <li key={`${o.postcode}-${o.suburb}-${o.state}`} {...rest}>
                        {o.suburb} {o.state} {o.postcode}
                    </li>
                );
            }}
            inputValue={value}
            onInputChange={(_e, v, reason) => {
                // 'reset' fires when an option is chosen; onChange below handles that.
                if (reason === 'input' || reason === 'clear') {
                    onChange(v);
                }
            }}
            onChange={(_e, v) => {
                if (v !== null && typeof v !== 'string') {
                    onSelect(v);
                }
            }}
            loading={loading}
            noOptionsText={value.trim().length < 2 ? 'Type a suburb or postcode' : 'No matching suburbs'}
            loadingText="Searching..."
            // Cast once: under exactOptionalPropertyTypes MUI's render params are not
            // assignable to TextFieldProps (optional className/size), though identical at runtime.
            renderInput={(params) => <TextField {...(params as TextFieldProps)} size="small" label={label} />}
        />
    );
}
